/**
 * Static page route definitions — each maps a path to a StaticPage instance.
 * Admin can edit hero_title, hero_subtitle_html, body_html for each via the
 * "Site Content" tab → Pages & Hero Copy.
 */
import React from 'react';
import { StaticPage } from './StaticPage';

const PAGES = [
  {
    path: '/about',
    pageId: 'about',
    tag: 'OUR STORY',
    defaultTitle: 'About localliink.com',
    defaultSubtitle: 'Built in India, for travellers who want real prices, real properties and real cashback on every stay.',
    defaultBodyHtml: `
      <p>localliink.com is an independent multi-hotel marketplace that puts Indian travellers and hotel owners on equal footing — no hidden fees, no middlemen.</p>
      <h2>Why we exist</h2>
      <p>For too long, hotel bookings in India have meant inflated prices and opaque commissions. We're changing that with a wallet-first reward system, transparent commission rates and a built-in certification programme that holds independent hotels to a high standard.</p>
      <h2>Where we're based</h2>
      <p>Headquartered in Chandigarh with team members across Punjab, Haryana &amp; Delhi NCR.</p>
    `,
  },
  {
    path: '/press',
    pageId: 'press',
    tag: 'PRESS CENTRE',
    defaultTitle: 'Press Centre',
    defaultSubtitle: 'Coverage, brand assets and a direct line to our founders.',
    defaultBodyHtml: `<p>For media enquiries, please write to <a href="mailto:press@localliink.com">press@localliink.com</a>. Brand assets and the latest fact-sheet are available on request.</p>`,
  },
  {
    path: '/careers',
    pageId: 'careers',
    tag: 'CAREERS',
    defaultTitle: 'Join the team',
    defaultSubtitle: "We're hiring across engineering, operations, hospitality &amp; growth.",
    defaultBodyHtml: `<p>We don't post every open role online. If you're passionate about Indian hospitality and want to build something meaningful, send us your resume — we read every submission.</p>`,
    contactForm: 'careers',
  },
  {
    path: '/investor-relations',
    pageId: 'investor-relations',
    tag: 'INVESTORS',
    defaultTitle: 'Investor Relations',
    defaultSubtitle: 'Quarterly updates and fundamentals for accredited investors.',
    defaultBodyHtml: `<p>For investor enquiries, please reach out to <a href="mailto:ir@localliink.com">ir@localliink.com</a>. We share metrics, runway and pipeline updates every quarter to current and prospective investors.</p>`,
  },
  {
    path: '/trust-safety',
    pageId: 'trust-safety',
    tag: 'TRUST & SAFETY',
    defaultTitle: 'Trust &amp; Safety',
    defaultSubtitle: 'How we keep guests and hotel owners safe on localliink.com.',
    defaultBodyHtml: `
      <h2>For travellers</h2>
      <ul>
        <li>Every booking is verified with WhatsApp + SMS confirmation.</li>
        <li>Free cancellation up to 24 hours before check-in on most properties.</li>
        <li>₹0 platform fees — what you see is what you pay.</li>
      </ul>
      <h2>For hotel owners</h2>
      <ul>
        <li>Payouts processed within 7 days of guest check-out.</li>
        <li>Bot &amp; fraud detection on every booking attempt.</li>
        <li>Dedicated dispute desk reachable 7 days a week.</li>
      </ul>
    `,
  },
  {
    path: '/cancellation-policy',
    pageId: 'cancellation-policy',
    tag: 'CANCELLATION',
    defaultTitle: 'Cancellation Policy',
    defaultSubtitle: 'Most properties offer free cancellation. Here are the details.',
    defaultBodyHtml: `
      <ul>
        <li><strong>Free cancellation</strong> up to 24 hours before check-in — full refund to original payment method or wallet.</li>
        <li><strong>Within 24 hours</strong> — first night charge, refund within 7 business days.</li>
        <li><strong>No-show</strong> — full booking amount charged.</li>
      </ul>
      <p>Properties may set custom rules visible on the room card before you book.</p>
    `,
  },
  {
    path: '/refund-status',
    pageId: 'refund-status',
    tag: 'REFUNDS',
    defaultTitle: 'Refund Status',
    defaultSubtitle: 'Track the status of any refund issued by localliink.com.',
    defaultBodyHtml: `<p>Refunds typically settle in 5–7 business days. To check yours, head to <a href="/my-bookings">My Bookings</a> and select the booking — the refund timeline is shown next to the cancellation receipt.</p>`,
  },
  {
    path: '/travel-guides',
    pageId: 'travel-guides',
    tag: 'TRAVEL GUIDES',
    defaultTitle: 'Travel guides &amp; city stories',
    defaultSubtitle: 'Reader-submitted guides to Punjab, Haryana &amp; Chandigarh — written by people who actually stayed there.',
    defaultBodyHtml: `<p>Got a story about your last trip? Pitch us at <a href="mailto:stories@localliink.com">stories@localliink.com</a> — we feature one reader guide a week and pay ₹500 cashback for published pieces.</p>`,
  },
  {
    path: '/loved-by-travellers',
    pageId: 'loved-by-travellers',
    tag: 'TRAVELLER STORIES',
    defaultTitle: 'Loved by travellers',
    defaultSubtitle: 'Real stays. Real stories. Curated from across the network.',
    defaultBodyHtml: `<p>Browse highlights from our top-rated stays. Have a story to share? Add it from <a href="/my-bookings">My Bookings</a> after your trip.</p>`,
  },
  {
    path: '/connect',
    pageId: 'connect',
    tag: 'CONNECT',
    defaultTitle: 'Connect with localliink.com',
    defaultSubtitle: 'Reach our team via WhatsApp, email, or the channel below.',
    defaultBodyHtml: `
      <ul>
        <li><strong>WhatsApp:</strong> <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer">+91 98765 43210</a></li>
        <li><strong>Email:</strong> <a href="mailto:hello@localliink.com">hello@localliink.com</a></li>
        <li><strong>Office:</strong> SCO 56, Sector 17, Chandigarh — open Mon–Sat 10 AM – 7 PM.</li>
      </ul>
    `,
  },
  {
    path: '/customer-support',
    pageId: 'customer-support',
    tag: 'SUPPORT',
    defaultTitle: 'Customer Support',
    defaultSubtitle: 'We reply within 24 hours, every day.',
    defaultBodyHtml: `<p>Need help with a booking, cancellation or wallet credit? Drop us a note below and our team will email you back the same day.</p>`,
    contactForm: 'support',
  },
  {
    path: '/explore',
    pageId: 'explore',
    tag: 'EXPLORE',
    defaultTitle: 'Explore',
    defaultSubtitle: 'Discover stays, programmes &amp; offers across India.',
    defaultBodyHtml: `<ul><li><a href="/insights">Insights &amp; data</a></li><li><a href="/reviews">Reviews</a></li><li><a href="/travel-guides">Travel guides</a></li><li><a href="/loved-by-travellers">Loved by travellers</a></li></ul>`,
  },
  {
    path: '/spend-wallet-credits',
    pageId: 'spend-wallet-credits',
    tag: 'WALLET',
    defaultTitle: 'Spend your wallet credits',
    defaultSubtitle: 'Use the localliink.com wallet on any future booking — no expiry on earned cashback.',
    defaultBodyHtml: `
      <h2>How the wallet works</h2>
      <ul>
        <li>Every completed booking, review and referral earns you wallet credits in ₹.</li>
        <li>Credits can be redeemed on any future stay — up to 100% of the booking amount.</li>
        <li>Earned credits never expire. <em>Promotional credits</em> (e.g. sign-up bonus) have a 30-day window — shown on the homepage strip.</li>
        <li>Apply credits at checkout on the hotel detail page — just enter your guest code.</li>
      </ul>
      <h2>How to earn more</h2>
      <ul>
        <li><strong>Complete a stay</strong> — 5% cashback on the booking amount.</li>
        <li><strong>Leave an honest review</strong> (above 6/10) — ₹10 instantly + ₹10 more for a written comment.</li>
        <li><strong>Refer a friend</strong> — ₹50 when they book their first stay.</li>
      </ul>
    `,
  },
];

export const staticPageRoutes = PAGES;
export const StaticPageRouteList = () => PAGES.map((p) => ({
  ...p,
  element: (
    <StaticPage
      pageId={p.pageId}
      defaultTitle={p.defaultTitle}
      defaultSubtitle={p.defaultSubtitle}
      defaultBodyHtml={p.defaultBodyHtml}
      tag={p.tag}
      contactForm={p.contactForm}
    />
  ),
}));
